import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Checkbox,
  FormGroup,
  TextareaAutosize,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Timer,
  Upload,
  Save,
  Send,
  Warning,
  CheckCircle,
  Code,
  Description,
  Quiz,
  Assignment,
  CloudUpload,
  Delete,
  Visibility,
  ArrowBack,
  PlayArrow,
  Pause,
  AttachFile,
  Link,
  Edit,
  Info,
  CalendarToday
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const TaskAssessment = () => {
  const { applicationId, taskId } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Timer states - for tracking session time, not countdown
  const [submissionData, setSubmissionData] = useState(null);
  const [timeSpent, setTimeSpent] = useState(0);
  const intervalRef = useRef(null);
  
  // Submission states based on task type
  const [textAnswer, setTextAnswer] = useState('');
  const [codeAnswer, setCodeAnswer] = useState('');
  const [selectedOptions, setSelectedOptions] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [projectLink, setProjectLink] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState(''); // New field for additional information
  
  // Confirmation dialog
  const [confirmDialog, setConfirmDialog] = useState(false);
  
  // File upload ref
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchTaskDetails();
    startOrResumeTask();
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [taskId, applicationId]);

  useEffect(() => {
    // Track time spent based on submission start time
    if (submissionData && submissionData.startedAt && !submitted) {
      const updateTimer = () => {
        const startTime = new Date(submissionData.startedAt).getTime();
        const currentTime = Date.now();
        const elapsed = Math.floor((currentTime - startTime) / 1000);
        setTimeSpent(elapsed);
      };
      
      // Update immediately
      updateTimer();
      
      // Then update every second
      intervalRef.current = setInterval(updateTimer, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [submissionData, submitted]);

  const startOrResumeTask = async () => {
    const token = localStorage.getItem('token');
    
    try {
      // Check if there's an existing submission
      const checkResponse = await axios.get(
        `http://localhost:5000/api/tasks/submission/check/${taskId}/${applicationId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (checkResponse.data.data) {
        // Resume existing submission
        const submission = checkResponse.data.data;
        setSubmissionData(submission);
        
        // Restore previous answers if in-progress
        if (submission.status === 'in-progress' && submission.answers) {
          if (submission.answers.essay) setTextAnswer(submission.answers.essay);
          if (submission.answers.code) setCodeAnswer(submission.answers.code.solution);
          if (submission.answers.multipleChoice) setSelectedOptions(submission.answers.multipleChoice);
          if (submission.answers.urls && submission.answers.urls[0]) {
            setProjectLink(submission.answers.urls[0].url);
          }
        }
      }
    } catch (err) {
      // No existing submission, create a new one
      try {
        const startResponse = await axios.post(
          'http://localhost:5000/api/tasks/submission/start',
          {
            taskId,
            applicationId
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        setSubmissionData(startResponse.data.data);
      } catch (startErr) {
        console.error('Error starting task:', startErr);
        // Continue without submission tracking if it fails
      }
    }
  };

  const fetchTaskDetails = async () => {
    const token = localStorage.getItem('token');
    
    try {
      setLoading(true);
      
      // For candidates, we'll fetch the task directly without requiring a submission
      // The backend will handle access control
      const response = await axios.get(`http://localhost:5000/api/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTask(response.data.data);
      
      // Initialize answer based on task type
      if (response.data.data.taskType === 'multiple-choice' && response.data.data.questions) {
        const initialAnswers = {};
        response.data.data.questions.forEach(q => {
          initialAnswers[q._id || q.id] = '';
        });
        setSelectedOptions(initialAnswers);
      }
    } catch (err) {
      console.error('Error fetching task:', err);
      
      // If 403, try a different approach - directly use task data if available
      if (err.response?.status === 403) {
        // Try to fetch through the application context
        try {
          const appResponse = await axios.get(
            `http://localhost:5000/api/applications/${applicationId}`,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );
          
          // Get job tasks and find the matching one
          const jobResponse = await axios.get(
            `http://localhost:5000/api/tasks/job/${appResponse.data.data.job._id || appResponse.data.data.job}`,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );
          
          const taskData = jobResponse.data.data.find(t => t._id === taskId);
          if (taskData) {
            setTask(taskData);
            
            // Initialize answer based on task type
            if (taskData.taskType === 'multiple-choice' && taskData.questions) {
              const initialAnswers = {};
              taskData.questions.forEach(q => {
                initialAnswers[q._id || q.id] = '';
              });
              setSelectedOptions(initialAnswers);
            }
          } else {
            setError('Task not found');
          }
        } catch (altErr) {
          console.error('Alternative fetch error:', altErr);
          setError('Failed to load task details. Please ensure you have access to this task.');
        }
      } else {
        setError('Failed to load task details');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    }
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    }
    return `${minutes}m ${secs}s`;
  };

  const formatDuration = (task) => {
    // Handle new duration format
    if (task?.duration && task.duration.value && task.duration.unit) {
      const { value, unit } = task.duration;
      
      if (value === 1) {
        // Singular forms
        if (unit === 'hours') return '1 hour';
        if (unit === 'days') return '1 day';
        if (unit === 'weeks') return '1 week';
      }
      
      // Plural forms
      return `${value} ${unit}`;
    }
    
    // Handle old timeLimit format (in days)
    if (task?.timeLimit) {
      if (task.timeLimit === 1) return '1 day';
      if (task.timeLimit === 7) return '1 week';
      if (task.timeLimit === 14) return '2 weeks';
      if (task.timeLimit === 30) return '1 month';
      return `${task.timeLimit} days`;
    }
    
    // Default fallback
    return '7 days';
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        setError(`File ${file.name} exceeds 10MB limit`);
        return false;
      }
      return true;
    });
    
    setUploadedFiles([...uploadedFiles, ...validFiles]);
    setError('');
  };

  const removeFile = (index) => {
    setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
  };

  const handleQuizAnswer = (questionId, answer) => {
    setSelectedOptions({
      ...selectedOptions,
      [questionId]: answer
    });
  };

  const handleSubmit = async () => {
    setConfirmDialog(false);

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      // Prepare submission data based on task type
      let submissionData = {
        task: taskId,
        application: applicationId,
        submittedAt: new Date(),
        timeTaken: timeSpent, // Time spent in seconds
        additionalInfo: additionalInfo // Include additional information
      };

      switch (task.taskType) {
        case 'multiple-choice':
          submissionData.answers = selectedOptions;
          submissionData.content = JSON.stringify(selectedOptions);
          break;
        case 'code':
          submissionData.code = codeAnswer;
          submissionData.content = codeAnswer;
          break;
        case 'project':
          submissionData.projectLink = projectLink;
          submissionData.content = projectLink;
          break;
        case 'url-submission':
          submissionData.projectLink = projectLink;
          submissionData.content = projectLink;
          break;
        case 'essay':
        case 'file-upload':
          submissionData.content = textAnswer;
          break;
        default:
          submissionData.content = textAnswer;
      }

      // If there are files to upload, handle them
      if (uploadedFiles.length > 0) {
        const formData = new FormData();
        Object.keys(submissionData).forEach(key => {
          if (submissionData[key] !== undefined && submissionData[key] !== null) {
            formData.append(key, typeof submissionData[key] === 'object' ? JSON.stringify(submissionData[key]) : submissionData[key]);
          }
        });
        uploadedFiles.forEach(file => {
          formData.append('files', file);
        });

        await axios.post(
          'http://localhost:5000/api/tasks/submit',
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      } else {
        await axios.post(
          'http://localhost:5000/api/tasks/submit',
          submissionData,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      }

      setSubmitted(true);
      
      // Show success message and redirect
      setTimeout(() => {
        navigate('/my-applications');
      }, 3000);
    } catch (err) {
      console.error('Error submitting task:', err);
      setError(err.response?.data?.message || 'Failed to submit task');
    } finally {
      setSubmitting(false);
    }
  };

  const renderTaskContent = () => {
    if (!task) return null;

    switch (task.taskType) {
      case 'multiple-choice':
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              <Quiz sx={{ verticalAlign: 'middle', mr: 1 }} />
              Multiple Choice Questions
            </Typography>
            {task.questions?.map((question, index) => (
              <Card key={question._id || index} sx={{ mb: 2 }}>
                <CardContent>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend">
                      {index + 1}. {question.question}
                    </FormLabel>
                    <RadioGroup
                      value={selectedOptions[question._id || question.id] || ''}
                      onChange={(e) => handleQuizAnswer(question._id || question.id, e.target.value)}
                    >
                      {question.options?.map((option, optIndex) => (
                        <FormControlLabel
                          key={optIndex}
                          value={option}
                          control={<Radio />}
                          label={option}
                          disabled={submitted}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </CardContent>
              </Card>
            ))}
          </Box>
        );

      case 'essay':
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              <Edit sx={{ verticalAlign: 'middle', mr: 1 }} />
              Essay/Writing Assignment
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="body1">
                {task.instructions || task.description}
              </Typography>
            </Paper>
            
            <TextField
              fullWidth
              multiline
              rows={15}
              variant="outlined"
              label="Your Essay Response"
              placeholder="Write your detailed response here..."
              value={textAnswer}
              onChange={(e) => setTextAnswer(e.target.value)}
              disabled={submitted}
            />
          </Box>
        );

      case 'code':
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              <Code sx={{ verticalAlign: 'middle', mr: 1 }} />
              Coding Challenge
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="body1" paragraph>
                {task.instructions || task.description}
              </Typography>
              {task.codeTemplate?.testCases && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Sample Test Cases:
                  </Typography>
                  <List dense>
                    {task.codeTemplate.testCases.filter(tc => !tc.isHidden).map((testCase, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={`Input: ${testCase.input}`}
                          secondary={`Expected Output: ${testCase.expectedOutput}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Paper>
            <TextField
              fullWidth
              multiline
              rows={15}
              variant="outlined"
              placeholder="Write your code here..."
              value={codeAnswer}
              onChange={(e) => setCodeAnswer(e.target.value)}
              disabled={submitted}
              sx={{
                fontFamily: 'monospace',
                fontSize: '14px'
              }}
            />
          </Box>
        );

      case 'file-upload':
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              <CloudUpload sx={{ verticalAlign: 'middle', mr: 1 }} />
              File Upload Assignment
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="body1">
                {task.instructions || task.description}
              </Typography>
            </Paper>
            
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                multiline
                rows={6}
                variant="outlined"
                label="Assignment Description (Optional)"
                placeholder="Describe your work or approach..."
                value={textAnswer}
                onChange={(e) => setTextAnswer(e.target.value)}
                disabled={submitted}
                sx={{ mb: 2 }}
              />
              
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                multiple
                onChange={handleFileUpload}
                disabled={submitted}
              />
              <Button
                variant="contained"
                startIcon={<CloudUpload />}
                onClick={() => fileInputRef.current?.click()}
                disabled={submitted}
                fullWidth
                size="large"
              >
                Upload Files (Max 10MB each)
              </Button>
              
              {uploadedFiles.length > 0 && (
                <List sx={{ mt: 2 }}>
                  {uploadedFiles.map((file, index) => (
                    <ListItem
                      key={index}
                      secondaryAction={
                        !submitted && (
                          <IconButton edge="end" onClick={() => removeFile(index)}>
                            <Delete />
                          </IconButton>
                        )
                      }
                    >
                      <ListItemIcon>
                        <AttachFile />
                      </ListItemIcon>
                      <ListItemText
                        primary={file.name}
                        secondary={`${(file.size / 1024).toFixed(2)} KB`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Box>
        );

      case 'url-submission':
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              <Link sx={{ verticalAlign: 'middle', mr: 1 }} />
              URL Submission
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="body1">
                {task.instructions || task.description}
              </Typography>
            </Paper>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Submission URL"
                  variant="outlined"
                  value={projectLink}
                  onChange={(e) => setProjectLink(e.target.value)}
                  disabled={submitted}
                  placeholder="https://..."
                  helperText="Enter the URL to your work (portfolio, demo, repository, etc.)"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  label="Description (Optional)"
                  placeholder="Describe what you're submitting..."
                  value={textAnswer}
                  onChange={(e) => setTextAnswer(e.target.value)}
                  disabled={submitted}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 'project':
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              <Assignment sx={{ verticalAlign: 'middle', mr: 1 }} />
              Project Submission
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="body1">
                {task.instructions || task.description}
              </Typography>
            </Paper>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Project Repository Link (GitHub, GitLab, etc.)"
                  variant="outlined"
                  value={projectLink}
                  onChange={(e) => setProjectLink(e.target.value)}
                  disabled={submitted}
                  placeholder="https://github.com/username/project"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  variant="outlined"
                  label="Project Description"
                  placeholder="Describe your project, technologies used, challenges faced, etc..."
                  value={textAnswer}
                  onChange={(e) => setTextAnswer(e.target.value)}
                  disabled={submitted}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Upload Project Files (Optional)
                </Typography>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  multiple
                  onChange={handleFileUpload}
                  disabled={submitted}
                />
                <Button
                  variant="outlined"
                  startIcon={<CloudUpload />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={submitted}
                  fullWidth
                >
                  Upload Additional Files
                </Button>
                
                {uploadedFiles.length > 0 && (
                  <List dense sx={{ mt: 1 }}>
                    {uploadedFiles.map((file, index) => (
                      <ListItem
                        key={index}
                        secondaryAction={
                          !submitted && (
                            <IconButton edge="end" onClick={() => removeFile(index)}>
                              <Delete />
                            </IconButton>
                          )
                        }
                      >
                        <ListItemIcon>
                          <AttachFile />
                        </ListItemIcon>
                        <ListItemText
                          primary={file.name}
                          secondary={`${(file.size / 1024).toFixed(2)} KB`}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              <Assignment sx={{ verticalAlign: 'middle', mr: 1 }} />
              Assignment
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="body1">
                {task.description}
              </Typography>
            </Paper>
            
            <TextField
              fullWidth
              multiline
              rows={12}
              variant="outlined"
              placeholder="Enter your response here..."
              value={textAnswer}
              onChange={(e) => setTextAnswer(e.target.value)}
              disabled={submitted}
            />
          </Box>
        );
    }
  };

  const getTaskIcon = () => {
    switch (task?.taskType) {
      case 'multiple-choice':
        return <Quiz />;
      case 'essay':
        return <Edit />;
      case 'code':
        return <Code />;
      case 'file-upload':
        return <CloudUpload />;
      case 'url-submission':
        return <Link />;
      case 'project':
        return <Assignment />;
      default:
        return <Assignment />;
    }
  };

  const getTaskTypeLabel = () => {
    switch (task?.taskType) {
      case 'multiple-choice':
        return 'Multiple Choice';
      case 'essay':
        return 'Essay/Writing';
      case 'code':
        return 'Coding';
      case 'file-upload':
        return 'File Upload';
      case 'url-submission':
        return 'URL Submission';
      case 'project':
        return 'Project';
      default:
        return 'Assignment';
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error && !task) {
    return (
      <Container>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/my-applications')} sx={{ mt: 2 }}>
          Back to Applications
        </Button>
      </Container>
    );
  }

  if (submitted) {
    return (
      <Container maxWidth="md">
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle color="success" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Task Submitted Successfully!
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            Your submission has been recorded. You will be redirected to the applications page.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/my-applications')}
            startIcon={<ArrowBack />}
          >
            Back to Applications
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Header with Task Info */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getTaskIcon()}
              <Typography variant="h5">
                {task?.title}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <Chip
                icon={getTaskIcon()}
                label={getTaskTypeLabel()}
                color="primary"
                size="small"
              />
              <Chip
                label={`${task?.difficulty || 'Medium'} Difficulty`}
                size="small"
                variant="outlined"
              />
              <Chip
                icon={<CalendarToday />}
                label={`Time Limit: ${formatDuration(task)}`}
                size="small"
                variant="outlined"
                color="info"
              />
              <Chip
                label={`${task?.maxScore || 100} points`}
                size="small"
                variant="outlined"
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="textSecondary">
                Time Elapsed
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                <Timer color="action" />
                <Typography variant="h6" color={timeSpent > 3600 ? 'warning.main' : 'text.primary'}>
                  {formatTime(timeSpent)}
                </Typography>
              </Box>
              <Typography variant="caption" color="textSecondary">
                {submissionData?.startedAt && (
                  <>Started: {new Date(submissionData.startedAt).toLocaleTimeString()}</>
                )}
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block">
                Time limit: {formatDuration(task)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Task Content */}
      <Paper sx={{ p: 3, mb: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {renderTaskContent()}
        
        {/* Additional Information Field (Common for all task types) */}
        <Box sx={{ mt: 4 }}>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="h6" gutterBottom>
            <Info sx={{ verticalAlign: 'middle', mr: 1 }} />
            Additional Information
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            label="Message to Evaluators (Optional)"
            placeholder="Any additional information you want to communicate to the evaluators regarding this assignment..."
            value={additionalInfo}
            onChange={(e) => setAdditionalInfo(e.target.value)}
            disabled={submitted}
            helperText="Use this space to explain your approach, any challenges faced, or special considerations"
          />
        </Box>
      </Paper>

      {/* Submit Button */}
      {!submitted && (
      <Paper sx={{ p: 2, position: 'sticky', bottom: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/my-applications')}
              startIcon={<ArrowBack />}
            >
              Save & Exit
            </Button>
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="textSecondary" display="block">
                  Session started {submissionData?.startedAt ? new Date(submissionData.startedAt).toLocaleString() : 'now'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Your progress is being tracked
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => setConfirmDialog(true)}
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} /> : <Send />}
              >
                {submitting ? 'Submitting...' : 'Submit Task'}
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>
          <Warning color="warning" /> Confirm Submission
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to submit your task? You won't be able to make changes after submission.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Session time: {formatTime(timeSpent)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>
            Continue Working
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            Submit Task
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TaskAssessment;
